import { flushSync } from "react-dom";

/**
 * Pasar de caso como un carrusel de verdad.
 *
 * ⚠️ Por qué NO se hace moviendo el contenedor con `transform`, que fue el
 * intento anterior: al no existir el caso vecino en pantalla, el que se iba
 * volaba un pedazo hacia un lado y el que llegaba entraba desde el otro, y
 * ENTRE LOS DOS quedaba un hueco con el fondo a la vista. Medido en el video
 * del 2026-07-29: el viejo salía 32 % del ancho y el nuevo entraba desde el
 * 32 % contrario, así que había 64 % de pantalla vacía. Eso es lo que se
 * sentía como que la ventana se caía en vez de pasar de largo.
 *
 * Aquí el navegador toma una foto del antes y otra del después y las mueve
 * PEGADAS, a exactamente un ancho de pantalla de distancia: nunca hay hueco.
 * Y como la foto la compone el compositor, no se vuelve a pintar la bitácora
 * completa en cada cuadro (era la otra queja: se sentía trabado).
 */

/** Hacia dónde queda el caso nuevo respecto al que se está viendo. */
export type DirCaso = "next" | "prev";

/** Lo que dura el carrusel. Corto a propósito: es navegar, no lucirse. */
const MS = 260;
/** Arranca rápido y frena suave, como si el dedo lo hubiera empujado. */
const CURVA = "cubic-bezier(.32,.72,0,1)";

type ViewTransition = {
  ready: Promise<void>;
  finished: Promise<void>;
  updateCallbackDone: Promise<void>;
  skipTransition: () => void;
};

type DocConTransicion = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => ViewTransition;
};

function prefiereQuieto(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Si el navegador sabe hacer el carrusel. Chrome 111+, Safari 18+, Firefox 133+. */
export function soportaCarrusel(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof (document as DocConTransicion).startViewTransition === "function"
  );
}

/**
 * Cambia de caso deslizando.
 *
 * `desde` es el punto donde quedó el contenido cuando se soltó el dedo (en px,
 * negativo si se jalaba hacia el caso siguiente). El deslizamiento ARRANCA ahí,
 * así que el movimiento del dedo y el del carrusel son uno solo y no se ve el
 * brinco de regresar a cero antes de salir. Con el botón o el teclado vale 0.
 *
 * Si el navegador no soporta la transición (o el usuario pidió menos
 * movimiento), cambia de caso sin animar: la navegación NUNCA depende de que
 * la animación se pueda hacer.
 */
export function navegarComoCarrusel(opts: {
  dir: DirCaso;
  /** Lo que cambia la pantalla: navegar y subir al inicio. */
  aplicar: () => void;
  desde?: number;
}): void {
  const { dir, aplicar, desde = 0 } = opts;
  const doc = document as DocConTransicion;
  if (!doc.startViewTransition || prefiereQuieto()) {
    aplicar();
    return;
  }

  const raiz = document.documentElement;
  // Marca la dirección para el CSS (apaga el cross-fade que pone el navegador).
  raiz.dataset.casoNav = dir;

  const t = doc.startViewTransition(() => {
    // `flushSync` es el punto fino: el navegador toma la foto del «después»
    // en cuanto este callback termina. Sin forzar el pintado aquí, React
    // aplicaría el cambio de ruta más tarde y la transición acabaría animando
    // el caso VIEJO contra sí mismo (o contra un «Cargando…»).
    flushSync(aplicar);
  });

  const ancho = window.innerWidth || 390;
  // Hacia dónde se va el que estaba: a la izquierda si vamos al siguiente.
  const fuera = dir === "next" ? -ancho : ancho;

  void t.ready
    .then(() => {
      const comun = { duration: MS, easing: CURVA, fill: "both" as const };
      // El que se va: del punto donde iba el dedo hasta salirse de cuadro.
      raiz.animate(
        [{ transform: `translateX(${desde}px)` }, { transform: `translateX(${fuera}px)` }],
        { ...comun, pseudoElement: "::view-transition-old(root)" },
      );
      // El que llega: pegado al anterior (un ancho exacto de distancia) hasta
      // su lugar. `desde - fuera` es lo que los mantiene juntos: sin eso
      // aparece el hueco que se sentía como la caída.
      raiz.animate(
        [{ transform: `translateX(${desde - fuera}px)` }, { transform: "translateX(0)" }],
        { ...comun, pseudoElement: "::view-transition-new(root)" },
      );
    })
    .catch(() => {
      // `ready` se rechaza si la transición se salta (otra navegación encima).
      // No es un error: el cambio de caso ya ocurrió igual.
    });

  void t.finished.finally(() => {
    // Solo limpia si nadie arrancó otra transición mientras corría esta.
    if (raiz.dataset.casoNav === dir) delete raiz.dataset.casoNav;
  });
}
